import { AddRounded } from "@mui/icons-material";
import { Box, Button, IconButton, Skeleton, Stack, SxProps, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Accordion } from "./Accordion";
import { getViewListBackgroundColor, ViewList, ViewListColor } from "./ViewList";

interface IActiveItemsListProps<T> {
  draggablePlaceholder?: React.ReactNode;
  divider?: React.ReactNode;
  activeItemsSortFunction: (a: T, b: T) => number;
}

interface IClosedItemsListProps<T> {
  closedItemsFilter: (item: T) => boolean;
  closedItemsSortFunction: (a: T, b: T) => number;
}

interface IProps<T> {
  title: string;
  icon?: React.ReactNode;
  dataSource?: T[];
  isLoading: boolean;
  isExpanded?: boolean;
  color?: ViewListColor;
  divider?: React.ReactNode;
  renderContent?: (item: T[], isClosed: boolean) => React.ReactNode;
  renderSkeleton?: (index: number) => React.ReactNode;
  renderItem: (item: T, index: number, isClosed: boolean) => React.ReactNode;
  activeItemsListProps: IActiveItemsListProps<T>;
  closedItemsListProps?: IClosedItemsListProps<T>;
  onAddClicked?: (e: React.MouseEvent) => void;
  onExpandClicked?: () => void;
  sx?: SxProps;
}

const AccordionViewList = <T extends unknown>(props: IProps<T>) => {
  const color = props.color ?? ViewListColor.Teal;
  const [showClosed, setShowClosed] = useState(false);
  const [isExpandedInternal, setIsExpandedInternal] = useState(props.isExpanded ?? true);

  useEffect(() => {
    if (props.isExpanded !== undefined) {
      setIsExpandedInternal(props.isExpanded);
    }
  }, [props.isExpanded]);

  function getItemCountString() {
    if (props.closedItemsListProps !== undefined) {
      let activeItemsCount = props.dataSource?.filter(
        (item) => !props.closedItemsListProps!.closedItemsFilter(item)
      ).length;

      let closedItemsCount = props.dataSource?.filter((item) =>
        props.closedItemsListProps!.closedItemsFilter(item)
      ).length;

      return `${activeItemsCount} Active, ${closedItemsCount} Closed`;
    } else {
      return `${props.dataSource?.length} ${props.dataSource?.length! > 1 ? "Items" : "Item"}`;
    }
  }

  return (
    <Box>
      <Accordion
        title={
          <Stack direction="row" spacing={0.25} flexGrow={1} alignItems="top">
            {props.icon && <Box sx={{ display: "flex", alignSelf: "center" }}>{props.icon}</Box>}
            <Typography variant="body2" sx={{ color: "grey.400" }}>
              {props.title}
            </Typography>
            {!isExpandedInternal && (
              <Typography variant="caption" sx={{ position: "relative", top: 2 }}>
                {getItemCountString()}
              </Typography>
            )}
          </Stack>
        }
        hoverActions={
          props.onAddClicked && (
            <IconButton size="small" onClick={props.onAddClicked}>
              <AddRounded />
            </IconButton>
          )
        }
        expanded={isExpandedInternal}
        hideTitleUnderline={!isExpandedInternal}
        onExpandClicked={() => {
          if (showClosed && isExpandedInternal) {
            setShowClosed(false);
          }

          if (props.onExpandClicked) {
            props.onExpandClicked();
          } else {
            setIsExpandedInternal((value) => !value);
          }
        }}
        backgroundColorOnHover={getViewListBackgroundColor(color)}
      >
        <ViewList
          dataSource={props.dataSource}
          isLoading={props.isLoading}
          divider={props.divider}
          color={props.color}
          activeItemsListProps={props.activeItemsListProps}
          closedItemsListProps={
            props.closedItemsListProps
              ? {
                  ...props.closedItemsListProps,
                  showClosed: showClosed,
                  renderShowClosedButton: (params) => {
                    return (
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => {
                          params.onClick();
                          setShowClosed(!showClosed);
                        }}
                        sx={{ alignSelf: "flex-start", m: 0.5, ml: 1 }}
                      >
                        {!params.closedItemsVisible ? `Show Closed (${params.closedItemCount})` : `Hide Closed`}
                      </Button>
                    );
                  },
                }
              : undefined
          }
          renderSkeleton={(index) => <Skeleton variant="rectangular" key={index} height={24} sx={{ mx: 1, my: 0.5 }} />}
          renderItem={props.renderItem}
          renderContent={props.renderContent}
        />
      </Accordion>
    </Box>
  );
};

export { AccordionViewList };
